import React from 'react';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import type { LensDataset, LensFilter } from '~/types/leLens';

import { datasetsMissingColumn } from './filterColumnCoverage';
import { humaniseFilterLabel } from './filterLabels';

/** A single place to inspect and manage Lens-wide filters. */
export default function FiltersDialog({
  open,
  filters,
  isSaving,
  onClose,
  onEdit,
  onDelete,
  datasets = [],
}: {
  open: boolean;
  filters: Array<LensFilter>;
  isSaving: boolean;
  onClose: () => void;
  onEdit: (filter: LensFilter) => void;
  onDelete: (filter: LensFilter) => void;
  /** Every dataset on the board (F9), so a filter bound to a column not every dataset returns can
   *  say which ones it does not reach. Omitted or empty shows the list as it read before. */
  datasets?: ReadonlyArray<LensDataset>;
}) {
  return (
    <Dialog fullWidth maxWidth="sm" onClose={isSaving ? undefined : onClose} open={open}>
      <DialogTitle>Lens filters</DialogTitle>
      <DialogContent>
        {filters.length === 0 ? (
          <Alert severity="info">No Lens filters yet. Add one to let readers refine linked cards.</Alert>
        ) : (
          <List disablePadding>
            {filters.map((filter) => (
              <ListItem
                divider
                key={filter.id}
                secondaryAction={
                  <Stack direction="row" spacing={0.25}>
                    <Tooltip title={`Edit ${filter.label}`}>
                      <span>
                        <IconButton
                          aria-label={`Edit ${filter.label}`}
                          disabled={isSaving}
                          onClick={() => onEdit(filter)}
                          size="small"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={`Delete ${filter.label}`}>
                      <span>
                        <IconButton
                          aria-label={`Delete ${filter.label}`}
                          disabled={isSaving}
                          onClick={() => onDelete(filter)}
                          size="small"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                }
              >
                <ListItemText
                  primary={filter.label}
                  secondary={
                    <>
                      <Typography component="span" variant="body2">
                        {filter.type === 'dateRange' ? 'Date range' : filter.multiple ? 'Multi-select' : 'Dropdown'}
                      </Typography>
                      {filter.column && ` · Field: ${filter.column}`}
                      {filter.column && datasets.length > 0 && (
                        <Box sx={{ mt: 0.5 }}>
                          <Stack direction="row" flexWrap="wrap" gap={0.5}>
                            {datasetsMissingColumn(datasets, filter.column).map((dataset) => (
                              <Chip
                                key={dataset.id}
                                label={`${dataset.name}: not filtered by ${humaniseFilterLabel(filter.column as string)}`}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Stack>
                        </Box>
                      )}
                    </>
                  }
                  secondaryTypographyProps={{ component: 'div' }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button disabled={isSaving} onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
